import { ALL_TRIPS_TRIP_ID } from 'src/routes/trip-analyzer/data/trip-analyzer.constants';
import { shiftTimeWindowToServiceDate } from 'src/routes/trip-analyzer/data/timeWindow';
import {
    isAllTrips,
    normalizeTripIds,
    pruneObservedTripIdsForTimeWindow,
    shouldPreserveCompareCorridorOnDateChange,
} from 'src/routes/trip-analyzer/data/tripSelection';
import { getInitialTripAnalyzerState } from 'src/routes/trip-analyzer/data/trip-analyzer.actions';
import { catalogKinds, types } from 'src/routes/trip-analyzer/data/trip-analyzer.types';

const clearCatalog = (catalog, kinds) => {
    const nextCatalog = { ...catalog };

    kinds.forEach((kind) => {
        nextCatalog[kind] = {
            items: [],
            loading: false,
            error: null,
        };
    });

    return nextCatalog;
};

const clearTimeline = (timeline) => ({
    ...timeline,
    data: null,
    loading: false,
    error: null,
    message: null,
});

const clearComparePreview = (comparePreview) => ({
    ...comparePreview,
    data: null,
    loading: false,
    error: null,
    message: null,
});

const applyQueryCascade = (state, nextQuery) => {
    const previousQuery = state.query;
    let nextState = {
        ...state,
        query: {
            ...state.query,
            ...nextQuery,
        },
    };

    if (
        nextQuery.serviceDate !== undefined
        && nextQuery.serviceDate !== previousQuery.serviceDate
    ) {
        const preserveCompareCorridor = shouldPreserveCompareCorridorOnDateChange(previousQuery);
        const shiftedTimeWindow = shiftTimeWindowToServiceDate(
            nextQuery.serviceDate,
            previousQuery.timeWindowStart,
            previousQuery.timeWindowEnd,
        );

        nextState = {
            ...nextState,
            catalog: clearCatalog(
                nextState.catalog,
                preserveCompareCorridor
                    ? [catalogKinds.DIRECTIONS, catalogKinds.TRIPS]
                    : [
                        catalogKinds.ROUTES,
                        catalogKinds.DIRECTIONS,
                        catalogKinds.TRIPS,
                    ],
            ),
            columns: {
                items: [],
                loading: false,
                error: null,
                cacheKey: null,
            },
            timeline: clearTimeline(nextState.timeline),
            comparePreview: clearComparePreview(nextState.comparePreview),
            query: {
                ...nextState.query,
                routeKey: preserveCompareCorridor ? previousQuery.routeKey : null,
                directionId: preserveCompareCorridor ? previousQuery.directionId : null,
                observedTripIds: preserveCompareCorridor ? [] : [ALL_TRIPS_TRIP_ID],
                compareTrips: preserveCompareCorridor ? (previousQuery.compareTrips ?? []) : [],
                compareVisibleTripKeys: preserveCompareCorridor
                    ? (previousQuery.compareVisibleTripKeys ?? [])
                    : [],
                compareServiceDate: preserveCompareCorridor ? previousQuery.compareServiceDate : null,
                compareObservedTripId: preserveCompareCorridor
                    ? previousQuery.compareObservedTripId
                    : null,
                partnerKeys: [],
                timeWindowStart: shiftedTimeWindow.start,
                timeWindowEnd: shiftedTimeWindow.end,
            },
        };
    }

    if (
        nextQuery.routeKey !== undefined
        && nextQuery.routeKey !== previousQuery.routeKey
    ) {
        nextState = {
            ...nextState,
            catalog: clearCatalog(nextState.catalog, [
                catalogKinds.DIRECTIONS,
                catalogKinds.TRIPS,
            ]),
            columns: {
                items: [],
                loading: false,
                error: null,
                cacheKey: null,
            },
            timeline: clearTimeline(nextState.timeline),
            comparePreview: clearComparePreview(nextState.comparePreview),
            query: {
                ...nextState.query,
                directionId: null,
                observedTripIds: [ALL_TRIPS_TRIP_ID],
                partnerKeys: [],
            },
        };
    }

    if (
        nextQuery.directionId !== undefined
        && nextQuery.directionId !== previousQuery.directionId
    ) {
        nextState = {
            ...nextState,
            catalog: clearCatalog(nextState.catalog, [catalogKinds.TRIPS]),
            columns: {
                items: [],
                loading: false,
                error: null,
                cacheKey: null,
            },
            timeline: clearTimeline(nextState.timeline),
            comparePreview: clearComparePreview(nextState.comparePreview),
            query: {
                ...nextState.query,
                observedTripIds: [ALL_TRIPS_TRIP_ID],
                partnerKeys: [],
            },
        };
    }

    const timeWindowChanged = (
        (nextQuery.timeWindowStart !== undefined
            && nextQuery.timeWindowStart !== previousQuery.timeWindowStart)
        || (nextQuery.timeWindowEnd !== undefined
            && nextQuery.timeWindowEnd !== previousQuery.timeWindowEnd)
    );

    if (timeWindowChanged) {
        const prunedObservedTripIds = pruneObservedTripIdsForTimeWindow(
            nextState.query.observedTripIds,
            nextState.catalog.trips?.items ?? [],
            nextState.query.timeWindowStart,
            nextState.query.timeWindowEnd,
        );
        const previousIds = normalizeTripIds(nextState.query.observedTripIds).join('|');
        const prunedIds = normalizeTripIds(prunedObservedTripIds).join('|');

        if (previousIds !== prunedIds) {
            nextState = {
                ...nextState,
                query: {
                    ...nextState.query,
                    observedTripIds: prunedObservedTripIds,
                },
            };
        }
    }

    return nextState;
};

const tripAnalyzerReducer = (state = getInitialTripAnalyzerState(), action) => {
    switch (action.type) {
        case types.RESET_TRIP_ANALYZER:
            return getInitialTripAnalyzerState();

        case types.SET_QUERY:
            return applyQueryCascade(state, action.payload);

        case types.SET_COMPARE_ENABLED: {
            const nextState = applyQueryCascade(state, {
                compareEnabled: action.payload,
                compareTrips: action.payload ? state.query.compareTrips : [],
                compareVisibleTripKeys: action.payload ? state.query.compareVisibleTripKeys : [],
                compareServiceDate: action.payload ? state.query.compareServiceDate : null,
                compareObservedTripId: action.payload ? state.query.compareObservedTripId : null,
                observedTripIds: action.payload
                    ? (isAllTrips(state.query.observedTripIds)
                        ? []
                        : (normalizeTripIds(state.query.observedTripIds).length > 1
                            ? normalizeTripIds(state.query.observedTripIds).slice(0, 1)
                            : state.query.observedTripIds))
                    : [ALL_TRIPS_TRIP_ID],
                partnerKeys: action.payload ? state.query.partnerKeys : [],
            });

            if (action.payload) {
                return nextState;
            }

            return {
                ...nextState,
                catalog: {
                    ...nextState.catalog,
                    partners: [],
                },
                columns: {
                    items: [],
                    loading: false,
                    error: null,
                    cacheKey: null,
                },
                timeline: clearTimeline(nextState.timeline),
                comparePreview: clearComparePreview(nextState.comparePreview),
            };
        }

        case types.SET_TRIP_ORDER:
            return {
                ...state,
                tripOrderByCorridor: {
                    ...state.tripOrderByCorridor,
                    [action.payload.corridorKey]: action.payload.orderedTripIds,
                },
            };

        case types.OPEN_TSP_PANEL:
            return {
                ...state,
                tspPanel: {
                    open: true,
                    loading: false,
                    error: null,
                    marker: action.payload,
                    sessions: [],
                    events: [],
                },
            };

        case types.CLOSE_TSP_PANEL:
            return {
                ...state,
                tspPanel: {
                    open: false,
                    loading: false,
                    error: null,
                    marker: null,
                    sessions: [],
                    events: [],
                },
            };

        case types.SERVICE_DATES_REQUEST:
            return {
                ...state,
                serviceDates: {
                    ...state.serviceDates,
                    loading: true,
                    error: null,
                },
            };

        case types.SERVICE_DATES_SUCCESS:
            return {
                ...state,
                serviceDates: {
                    ...state.serviceDates,
                    loading: false,
                    error: null,
                    availableDates: action.payload.availableDates ?? state.serviceDates.availableDates,
                    minDate: action.payload.minDate ?? state.serviceDates.minDate,
                    maxDate: action.payload.maxDate ?? state.serviceDates.maxDate,
                },
            };

        case types.SERVICE_DATES_ERROR:
            return {
                ...state,
                serviceDates: {
                    ...state.serviceDates,
                    loading: false,
                    error: action.payload,
                },
            };

        case types.CATALOG_REQUEST:
            return {
                ...state,
                catalog: {
                    ...state.catalog,
                    [action.payload.kind]: {
                        ...state.catalog[action.payload.kind],
                        loading: true,
                        error: null,
                    },
                },
            };

        case types.CATALOG_SUCCESS:
            return {
                ...state,
                catalog: {
                    ...state.catalog,
                    [action.payload.kind]: {
                        items: action.payload.items ?? [],
                        loading: false,
                        error: null,
                    },
                    ...(action.payload.partners ? { partners: action.payload.partners } : {}),
                },
            };

        case types.CATALOG_ERROR:
            return {
                ...state,
                catalog: {
                    ...state.catalog,
                    [action.payload.kind]: {
                        ...state.catalog[action.payload.kind],
                        loading: false,
                        error: action.payload.error,
                    },
                },
            };

        case types.COLUMNS_REQUEST:
            return {
                ...state,
                columns: {
                    ...state.columns,
                    loading: true,
                    error: null,
                },
            };

        case types.COLUMNS_SUCCESS:
            return {
                ...state,
                columns: {
                    items: action.payload.items ?? [],
                    loading: false,
                    error: null,
                    cacheKey: action.payload.cacheKey ?? null,
                },
                catalog: {
                    ...state.catalog,
                    partners: action.payload.partners ?? state.catalog.partners,
                },
            };

        case types.COLUMNS_ERROR:
            return {
                ...state,
                columns: {
                    ...state.columns,
                    loading: false,
                    error: action.payload,
                },
            };

        case types.TIMELINE_REQUEST:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    loading: true,
                    error: null,
                    message: null,
                },
            };

        case types.TIMELINE_SUCCESS:
            return {
                ...state,
                timeline: {
                    data: action.payload,
                    loading: false,
                    error: null,
                    message: action.payload?.trips?.length ? null : 'No trips found for the selected filters.',
                },
            };

        case types.TIMELINE_ERROR:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    loading: false,
                    error: action.payload,
                    message: action.payload || 'Unable to load timeline.',
                },
            };

        case types.TIMELINE_RESET:
            return {
                ...state,
                timeline: clearTimeline(state.timeline),
            };

        case types.COMPARE_PREVIEW_REQUEST:
            return {
                ...state,
                comparePreview: {
                    ...state.comparePreview,
                    loading: true,
                    error: null,
                    message: null,
                },
            };

        case types.COMPARE_PREVIEW_SUCCESS:
            return {
                ...state,
                comparePreview: {
                    data: action.payload,
                    loading: false,
                    error: null,
                    message: action.payload?.trips?.length ? null : 'No trips found for the selected filters.',
                },
            };

        case types.COMPARE_PREVIEW_ERROR:
            return {
                ...state,
                comparePreview: {
                    ...state.comparePreview,
                    loading: false,
                    error: action.payload,
                    message: action.payload || 'Unable to load trip preview.',
                },
            };

        case types.CORRIDOR_REQUEST:
            return {
                ...state,
                corridorCache: {
                    ...state.corridorCache,
                    [action.payload.key]: {
                        status: 'loading',
                        data: null,
                        error: null,
                    },
                },
            };

        case types.CORRIDOR_SUCCESS:
            return {
                ...state,
                corridorCache: {
                    ...state.corridorCache,
                    [action.payload.key]: {
                        status: 'success',
                        data: action.payload.data,
                        error: null,
                    },
                },
            };

        case types.CORRIDOR_ERROR:
            return {
                ...state,
                corridorCache: {
                    ...state.corridorCache,
                    [action.payload.key]: {
                        status: 'error',
                        data: null,
                        error: action.payload.error,
                    },
                },
            };

        case types.TSP_EVENTS_REQUEST:
            return {
                ...state,
                tspPanel: {
                    ...state.tspPanel,
                    loading: true,
                    error: null,
                },
            };

        case types.TSP_EVENTS_SUCCESS:
            if (!state.tspPanel.open) {
                return state;
            }

            return {
                ...state,
                tspPanel: {
                    ...state.tspPanel,
                    loading: false,
                    error: null,
                    sessions: action.payload.sessions ?? [],
                    events: action.payload.events ?? [],
                },
            };

        case types.TSP_EVENTS_ERROR:
            if (!state.tspPanel.open) {
                return state;
            }

            return {
                ...state,
                tspPanel: {
                    ...state.tspPanel,
                    loading: false,
                    error: action.payload,
                },
            };

        default:
            return state;
    }
};

export default tripAnalyzerReducer;
